import React from 'react';
import { FormControl, MenuItem, Select, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { UnitOption } from '../../services/inventoryItemUnitService';
import { sameUnitName } from '../../utils/productionFormat';

interface SelectableUnit extends UnitOption {
  undefined?: boolean;
}

interface EntryUnitSelectProps {
  baseUnit: string;
  options: UnitOption[];
  value: string | null;
  factor?: number | null;
  disabled?: boolean;
  /** Tek seçenek varken açılır liste yerine düz metin. Üretim fişinde kapalı kalır. */
  plainWhenSingle?: boolean;
  onChange: (unitName: string | null, factor: number | null) => void;
}

const EntryUnitSelect: React.FC<EntryUnitSelectProps> = ({
  baseUnit,
  options,
  value,
  factor,
  disabled,
  plainWhenSingle = true,
  onChange,
}) => {
  const { t } = useTranslation('production');
  const baseName = (options.find((option) => option.isBase)?.unitName || baseUnit || '').trim();
  const list: SelectableUnit[] = options.length > 0
    ? options
    : (baseName ? [{ unitName: baseName, factor: 1, isBase: true, isDefaultForProduction: false }] : []);
  const selectedName = value && !sameUnitName(value, baseName) ? value : baseName;
  const known = list.some((option) => sameUnitName(option.unitName, selectedName));
  const choices: SelectableUnit[] = known || !value || sameUnitName(value, baseName)
    ? list
    : [...list, {
      unitName: value,
      factor: factor != null && factor > 0 ? factor : 1,
      isBase: false,
      isDefaultForProduction: false,
      undefined: true,
    }];
  const current = choices.find((option) => sameUnitName(option.unitName, selectedName)) ?? choices[0];

  const labelOf = (option: SelectableUnit) => (
    option.undefined ? `${option.unitName} ${t('units.undefined')}` : option.unitName
  );

  if (!current || disabled || (plainWhenSingle && choices.length <= 1)) {
    return <Typography variant="body2">{current ? labelOf(current) : '—'}</Typography>;
  }

  return (
    <FormControl size="small" sx={{ minWidth: 90 }}>
      <Select
        value={current.unitName}
        onChange={(event) => {
          const next = choices.find((option) => option.unitName === event.target.value);
          if (!next) return;
          if (next.isBase || sameUnitName(next.unitName, baseName)) onChange(null, null);
          else onChange(next.unitName, next.factor);
        }}
      >
        {choices.map((option) => (
          <MenuItem key={`${option.unitName}-${option.undefined ? 'x' : 'd'}`} value={option.unitName}>
            {labelOf(option)}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default EntryUnitSelect;
